import { lazy, Suspense, useEffect } from 'react'

import { createFileRoute } from '@tanstack/react-router'

import { ChevronDown } from 'lucide-react'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import {
	CHANNEL_METADATA,
	EDITABLE_CHANNELS,
} from '@/features/notifications/constants'
import { markOnboardingTaskAsComplete } from '@/features/onboarding/actions'
import { useOnboardingTask } from '@/features/onboarding/store'

import { ErrorBoundary } from '@/components/common/error-boundary'
import {
	SettingsCard,
	SettingsCardTitle,
} from '@/components/common/settings-card'
import { Skeleton } from '@/components/ui/skeleton'

const NotificationsSettings = lazy(
	() => import('@/features/notifications/components/preferences/settings'),
)

export const Route = createFileRoute('/_dashboard/settings/notifications')({
	component: NotificationsRoute,
	pendingComponent: NotificationsLoading,
	errorComponent: RouteErrorComponent,
	head: () => ({
		meta: [
			{
				title: 'Notifications | BitLyft AIR®',
			},
		],
	}),
})

function NotificationsRoute() {
	const notificationsTask = useOnboardingTask('reviewNotifications')

	useEffect(() => {
		if (!notificationsTask?.completed && !notificationsTask?.skipped) {
			markOnboardingTaskAsComplete('reviewNotifications')
		}
	}, [notificationsTask?.completed, notificationsTask?.skipped])

	return (
		<SettingsCard className="flex-col items-start sm:flex-row">
			<SettingsCardTitle>Notifications</SettingsCardTitle>

			<ErrorBoundary>
				<Suspense fallback={<NotificationsSwitchSkeleton />}>
					<NotificationsSettings />
				</Suspense>
			</ErrorBoundary>
		</SettingsCard>
	)
}

function NotificationsLoading() {
	return (
		<SettingsCard className="flex-col items-start sm:flex-row">
			<SettingsCardTitle>Notifications</SettingsCardTitle>

			<NotificationsSwitchSkeleton />
		</SettingsCard>
	)
}

function NotificationsSwitchSkeleton() {
	return (
		<div className="flex w-full max-w-(--breakpoint-sm) flex-col gap-4">
			{EDITABLE_CHANNELS.map((channel) => {
				const { label, description } = CHANNEL_METADATA[channel]
				return (
					<div
						key={channel}
						className="bg-background/50 flex w-full items-start justify-between gap-4 rounded-md p-4"
					>
						<div className="flex flex-col gap-1">
							<p className="font-medium">{label}</p>
							<p className="text-muted-foreground text-sm">{description}</p>
						</div>
						<div className="flex items-center gap-4">
							<Skeleton className="h-4 w-32" />
							<ChevronDown className="text-muted-foreground size-4" />
						</div>
					</div>
				)
			})}
		</div>
	)
}
